import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { fileUrl } from "../../lib/api";
import { useLang } from "../../i18n";

export function scrollToHash(href) {
    if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
}

export default function Navbar({ settings = {} }) {
    const { lang, setLang, t } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    const links = [
        { href: "#berita", label: t.nav_berita, id: "berita" },
        { href: "#atlet", label: t.nav_atlet, id: "atlet" },
        { href: "/prestasi", label: t.nav_prestasi, id: "prestasi", page: true },
        { href: "#gear", label: t.nav_gear, id: "gear" },
        { href: "#coach", label: t.nav_coach, id: "coach" },
        { href: "#jadwal", label: t.nav_jadwal, id: "jadwal" },
        { href: "#galeri", label: t.nav_galeri, id: "galeri" },
        { href: "#video", label: t.nav_video, id: "video" },
        { href: "#sponsor", label: t.nav_sponsor, id: "sponsor" },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => (e) => {
        e.preventDefault();
        setOpen(false);
        scrollToHash(href);
    };

    const linkCls = "text-xs uppercase tracking-[0.2em] text-white/75 hover:text-white transition-colors duration-300";
    const mobileLinkCls = "font-display uppercase text-2xl text-white hover:text-racing-gold transition-colors";

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 bg-[#D21E06] border-b border-[#B8190A] ${
                scrolled ? "shadow-lg shadow-black/20" : ""
            }`}
            data-testid="main-navbar"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
                <a href="#" onClick={(e) => { e.preventDefault(); window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo(0, 0); }} className="flex items-center shrink-0" data-testid="nav-logo">
                    {settings.logo_url ? (
                        <img src={fileUrl(settings.logo_url)} alt="Logo Mandalika Inlineskate" className="h-20 w-20 md:h-24 md:w-24 object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.25)]" />
                    ) : (
                        <span className="w-14 h-14 bg-white flex items-center justify-center font-display text-[#D21E06] text-3xl leading-none">M</span>
                    )}
                </a>

                <nav className="hidden md:flex items-center gap-5">
                    {links.map((l) =>
                        l.page ? (
                            <Link key={l.id} to={l.href} data-testid={`nav-link-${l.id}`} className={linkCls}>
                                {l.label}
                            </Link>
                        ) : (
                            <a key={l.id} href={l.href} onClick={go(l.href)} data-testid={`nav-link-${l.id}`} className={linkCls}>
                                {l.label}
                            </a>
                        )
                    )}
                    <a
                        href="/login"
                        data-testid="nav-parent-portal-link"
                        className="text-xs uppercase tracking-[0.2em] text-white/75 hover:text-white transition-colors duration-300"
                    >
                        {t.nav_parent_portal}
                    </a>
                    <div className="flex items-center border border-white/40" data-testid="lang-toggle">
                        <button
                            onClick={() => setLang("id")}
                            data-testid="lang-id-button"
                            className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${lang === "id" ? "bg-white text-[#D21E06]" : "text-white/70 hover:text-white"}`}
                        >
                            ID
                        </button>
                        <button
                            onClick={() => setLang("en")}
                            data-testid="lang-en-button"
                            className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${lang === "en" ? "bg-white text-[#D21E06]" : "text-white/70 hover:text-white"}`}
                        >
                            EN
                        </button>
                    </div>
                    <a
                        href="/daftar-siswa-baru"
                        data-testid="nav-new-student-button"
                        className="bg-white hover:bg-racing-gold hover:text-neutral-900 text-[#D21E06] text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300"
                    >
                        {t.nav_new_student}
                    </a>
                </nav>

                <div className="md:hidden flex items-center gap-3">
                    <div className="flex items-center border border-white/40" data-testid="lang-toggle-mobile">
                        <button
                            onClick={() => setLang("id")}
                            data-testid="lang-id-mobile"
                            className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.2em] ${lang === "id" ? "bg-white text-[#D21E06]" : "text-white/70"}`}
                        >
                            ID
                        </button>
                        <button
                            onClick={() => setLang("en")}
                            data-testid="lang-en-mobile"
                            className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.2em] ${lang === "en" ? "bg-white text-[#D21E06]" : "text-white/70"}`}
                        >
                            EN
                        </button>
                    </div>
                    <button
                        className="text-white"
                        onClick={() => setOpen(!open)}
                        data-testid="nav-mobile-toggle"
                        aria-label="Menu"
                    >
                        {open ? <X size={26} weight="bold" /> : <List size={26} weight="bold" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden overflow-hidden bg-[#D21E06] border-b border-[#B8190A]"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {links.map((l) =>
                                l.page ? (
                                    <Link key={l.id} to={l.href} onClick={() => setOpen(false)} data-testid={`nav-mobile-link-${l.id}`} className={mobileLinkCls}>
                                        {l.label}
                                    </Link>
                                ) : (
                                    <a key={l.id} href={l.href} onClick={go(l.href)} data-testid={`nav-mobile-link-${l.id}`} className={mobileLinkCls}>
                                        {l.label}
                                    </a>
                                )
                            )}
                            <a
                                href="/login"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-parent-portal-link"
                                className="font-display uppercase text-2xl text-white hover:text-racing-gold transition-colors"
                            >
                                {t.nav_parent_portal}
                            </a>
                            <a
                                href="/daftar-siswa-baru"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-new-student-button"
                                className="mt-2 bg-white text-[#D21E06] text-xs uppercase tracking-[0.2em] px-5 py-4 w-fit"
                            >
                                {t.nav_new_student}
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
